import { PayloadAction, ReducerCreators } from '@reduxjs/toolkit';

import { ProjectsState, GetProjectOptions, ProjectsDisplayView } from './types';
import { LoadingStatus, EventGroup, SelectedBrandliveEvent, EventGroupAssociation } from '@/types/projects.types';
import { GetProjects } from '@/connection/projects/get-projects';
import toastTrigger from '@ui/toast/toast-trigger';
import { GetProjectsFullList } from '@/connection/projects/get-projects-list';
import { GetProjectGroups } from '@/connection/projects/get-project-groups';
import { RootState } from '@/store/main';
import { AddProjectsToFolder } from '@/connection/projects/add-projects-to-folder';
import { DeleteProjectGroup } from '@/connection/projects/delete-project-group';
import { CreateProjectGroup } from '@/connection/projects/create-project-group';
import { UpdateProjectGroup } from '@/connection/projects/update-project-group';
import { DeleteProject } from '@/connection/projects/delete-project';
import DuplicateProject from '@/connection/projects/duplicate-projects';
import { EventGroupTreeEntry } from '@/types/projects.types';
import GetProjectGroupTree from '@/connection/projects/get-project-group-tree';
import ModifyProjectGroupsForAdmin, {
  ModifyProjectGroupsForAdminProps,
} from '@/connection/projects/modify-project-groups-for-admin';
import { uniq } from 'underscore';

export const reducers = (create: ReducerCreators<ProjectsState>) => ({
  clearProjects: create.reducer((state) => {
    state.projects = [];
    state.projectsTotals = null;
  }),
  loadProjects: create.asyncThunk(
    async (
      {
        channel,
      }: {
        channel: number;
      },
      { rejectWithValue, getState }
    ) => {
      try {
        const state = (getState() as RootState).projects;
        const results: Awaited<ReturnType<typeof GetProjects>> = await GetProjects({
          channel,
          limit: state.getProjectsOptions.pageSize,
          offset: state.getProjectsOptions.offset,
          sort: state.getProjectsOptions.sort,
          search: state.getProjectsOptions.searchCriteria,
          selectedEventGroupUuid: state.selectedProjectGroup,
          time: state.getProjectsOptions.time,
          templateEvents: false,
          excludeTestEvents: false,
        });

        const projects = results.events || [];
        const totals = results.totals || 0;

        return { projects, totals };
      } catch (e) {
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.projects = action.payload.projects as unknown as SelectedBrandliveEvent[];
        state.projectsTotals = action.payload.totals;
        state.projectsError = false;
      },
      pending: (state) => {
        state.projectsLoading = true;
      },
      rejected: (state, action) => {
        if (action.payload instanceof Error) {
          console.error(action.payload);
        } else {
          console.error(action.error);
        }
        state.projectsError = true;
        toastTrigger('There was an error loading projects. Please refresh the page.', {
          type: 'error',
          autoClose: 10000,
        });
      },
      settled: (state) => {
        state.projectsLoading = false;
      },
    }
  ),
  loadProjectsListForSeries: create.asyncThunk(
    async (
      {
        channel,
      }: {
        channel: number;
      },
      { rejectWithValue, getState }
    ) => {
      try {
        const state = (getState() as RootState).projects;
        const results: Awaited<ReturnType<typeof GetProjects>> = await GetProjects({
          channel,
          limit: state.getProjectsListForSeriesOptions.pageSize,
          offset: state.getProjectsListForSeriesOptions.offset,
          sort: state.getProjectsListForSeriesOptions.sort,
          search: state.getProjectsListForSeriesOptions.searchCriteria,
          selectedEventGroupUuid: state.selectedProjectGroup,
          time: state.getProjectsListForSeriesOptions.time,
          templateEvents: false,
          excludeTestEvents: false,
        });

        const projects = results.events || [];
        const totals = results.totals || 0;

        return { projects, totals };
      } catch (e) {
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        if (!state.getProjectsListForSeriesOptions.append) {
          state.projectsListForSeries = action.payload.projects as unknown as SelectedBrandliveEvent[];
        } else {
          state.projectsListForSeries = uniq(
            [...state.projectsListForSeries, ...(action.payload.projects as unknown as SelectedBrandliveEvent[])],
            (project) => project.uuid
          );
        }
        state.projectsError = false;
      },
      pending: (state) => {
        state.projectsLoading = true;
      },
      rejected: (state, action) => {
        if (action.payload instanceof Error) {
          console.error(action.payload);
        } else {
          console.error(action.error);
        }
        state.projectsError = true;
        toastTrigger('There was an error loading projects. Please refresh the page.', {
          type: 'error',
          autoClose: 10000,
        });
      },
      settled: (state) => {
        state.projectsLoading = false;
      },
    }
  ),
  setGetProjectOptions: create.reducer((state, action: PayloadAction<Partial<GetProjectOptions>>) => {
    if (state.projectsLoading) return state;

    state.getProjectsOptions = {
      ...state.getProjectsOptions,
      ...action.payload,
    };
  }),
  setGetProjectListForSeriesOptions: create.reducer((state, action: PayloadAction<Partial<GetProjectOptions>>) => {
    if (state.projectsLoading) return state;

    state.getProjectsListForSeriesOptions = {
      ...state.getProjectsListForSeriesOptions,
      ...action.payload,
    };
  }),
  deleteProject: create.asyncThunk(
    async (projectUuid: string, { rejectWithValue }) => {
      try {
        const deletedProject = await DeleteProject(projectUuid);
        return deletedProject;
      } catch (e) {
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.projectGroups) {
          state.projectGroups = state.projectGroups.map((pg) => ({
            ...pg,
            event_uuids: pg.event_uuids.filter((uuid) => uuid !== action.payload.uuid),
          }));

          const isOnlyProjectForGroup = state.projectGroups.filter(
            (pg) => pg.event_uuids.length === 1 && pg.event_uuids[0] === action.payload.uuid
          );

          if (isOnlyProjectForGroup.length) {
            state.selectedProjectGroup = '';
            state.projectGroups = [];
          }
        }

        state.projectToDelete = null;
      },
      rejected: (state, action) => {
        if (action.payload instanceof Error) {
          console.error(action.payload);
        } else {
          console.error(action.error);
        }
        state.projectsError = true;
        toastTrigger('There was an error deleting your project. Please refresh the page.', {
          type: 'error',
          autoClose: 10000,
        });
      },
    }
  ),
  setProjectToDelete: create.reducer((state, action: PayloadAction<string | null>) => {
    state.projectToDelete = action.payload;
  }),
  setProjectToEdit: create.reducer((state, action: PayloadAction<string | null>) => {
    state.projectToEdit = action.payload;
  }),
  duplicateProject: create.asyncThunk(
    async ({ projectUuid, channel }: { projectUuid: string; channel: number }, { rejectWithValue }) => {
      try {
        const result = await DuplicateProject({
          projectUuid,
          channel,
        });
        if ('error' in result) {
          throw rejectWithValue(result.error);
        }
        const { name, uuid } = result;
        return { name, uuid };
      } catch (e) {
        throw rejectWithValue(e);
      }
    },
    {
      pending: (state) => {
        state.projectsListLoading = true;
      },
      rejected: (_, action) => {
        console.error('Error duplicating project:', action.error);
        toastTrigger(
          `<div>
            <h4 class="H4">There was an error duplicating the project.</h4>
            <p class="S1">${action.payload instanceof Error ? action.payload.message : action.error.message}</p>
          </div>`,
          {
            type: 'error',
            autoClose: 5000,
          }
        );
      },
      settled: (state) => {
        state.projectsListLoading = false;
      },
    }
  ),
  loadProjectsList: create.asyncThunk(
    async ({ channel }: { channel: number }, { rejectWithValue }) => {
      try {
        const projectsList = await GetProjectsFullList(channel);
        return projectsList;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      pending: (state) => {
        state.projectsListLoading = true;
      },
      rejected: (state) => {
        state.projectsListError = true;
        toastTrigger('There was an error loading projects. Please refresh the page.', {
          type: 'error',
        });
      },
      fulfilled: (state, action) => {
        state.projectsList = action.payload;
        state.projectsListError = false;
      },
      settled: (state) => {
        state.projectsListLoading = false;
      },
    }
  ),
  loadProjectGroups: create.asyncThunk(
    async ({ channel }: { channel: number }, { rejectWithValue, getState }) => {
      try {
        const state = getState() as RootState;
        const projectGroups = state.project?.projectGroups as EventGroup[] | null;
        if (projectGroups) return projectGroups;

        const result = await GetProjectGroups({ channel });
        if ('error' in result) {
          console.error('loadProjectGroups error', result.error);
          throw rejectWithValue(result.error);
        }
        return (result || []).filter((group) => group.event_uuids.length > 0);
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      pending: (state) => {
        state.loadingProjectGroupsStatus = LoadingStatus.Loading;
      },
      fulfilled: (state, action) => {
        state.projectGroups = action.payload;
      },
      settled: (state) => {
        state.loadingProjectGroupsStatus = LoadingStatus.Done;
      },
    }
  ),
  setSelectedProjectGroup: create.reducer((state, action: PayloadAction<string>) => {
    state.selectedProjectGroup = action.payload;
  }),
  setProjectsDisplayView: create.reducer((state, action: PayloadAction<ProjectsDisplayView>) => {
    state.projectsDisplayView = action.payload;
  }),
  createProjectGroup: create.asyncThunk(
    async (
      {
        channel,
        name,
        projectUuids,
        adminIds,
      }: { channel: number; name: string; projectUuids: string[]; adminIds: number[] },
      { rejectWithValue }
    ) => {
      try {
        const newGroup = await CreateProjectGroup(channel, name, projectUuids, adminIds);
        return newGroup;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      rejected: (state, action) => {
        state.groupError = action.payload as string;
      },
      fulfilled: (state, action) => {
        state.groupError = '';
        if (state.projectGroups) {
          state.projectGroups = [...state.projectGroups, action.payload];
        }
      },
    }
  ),
  updateProjectGroup: create.asyncThunk(
    async (
      {
        channel,
        groupUuid,
        name,
        projectUuids,
        adminIds,
      }: { channel: number; groupUuid: string; name: string; projectUuids: string[]; adminIds: number[] },
      { rejectWithValue }
    ) => {
      try {
        const updatedGroup = await UpdateProjectGroup(channel, groupUuid, name, projectUuids, adminIds);
        return updatedGroup;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      rejected: (state, action) => {
        state.groupError = action.payload as unknown as string;
      },
      fulfilled: (state, action) => {
        state.groupError = '';
        if (state.projectGroups) {
          state.projectGroups = state.projectGroups.map((pg) =>
            pg.uuid === action.payload.uuid ? action.payload : pg
          );
          state.groupToEdit = null;
        }
      },
    }
  ),
  deleteProjectGroup: create.asyncThunk(
    async ({ channel, groupUuid }: { channel: number; groupUuid: string }, { rejectWithValue }) => {
      try {
        const res = DeleteProjectGroup(channel, groupUuid);
        return res;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.projectGroups) {
          const remainingProjectGroups = state.projectGroups.filter(
            (eg) => eg.uuid !== action.payload.deletedGroupUuid
          );

          return {
            ...state,
            projectGroups: remainingProjectGroups,
            groupToDelete: null,
          };
        } else {
          return state;
        }
      },
    }
  ),
  clearGroupError: create.reducer((state) => {
    state.groupError = '';
  }),
  setGroupToDelete: create.reducer((state, action: PayloadAction<string | null>) => {
    state.groupToDelete = action.payload;
  }),
  setGroupToEdit: create.reducer((state, action: PayloadAction<string | null>) => {
    state.groupToEdit = action.payload;
  }),
  setShowCreateGroupModal: create.reducer((state, action: PayloadAction<boolean>) => {
    state.showCreateGroupModal = action.payload;
  }),
  addProjectsToGroup: create.asyncThunk(
    async (
      { channel, groupUuid, projectUuids }: { channel: number; groupUuid: string; projectUuids: string[] },
      { rejectWithValue }
    ) => {
      try {
        const response = await AddProjectsToFolder({ channel, groupUuid, eventUuids: projectUuids });
        return response;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.projectGroups) {
          const groupUuid = action.payload[0].group_uuid;
          const eventUuids = action.payload.map((ega: EventGroupAssociation) => ega.event_uuid);

          return {
            ...state,
            addProjectsToGroup: [],
            eventGroups: state.projectGroups.map((pg) =>
              pg.uuid === groupUuid ? { ...pg, event_uuids: [...pg.event_uuids, ...eventUuids] } : pg
            ),
          };
        } else {
          return state;
        }
      },
    }
  ),
  setAddProjectsToGroup: create.reducer((state, action: PayloadAction<string[]>) => {
    state.addProjectsToGroup = action.payload;
  }),
  loadProjectGroupTree: create.asyncThunk(
    async ({ channel }: { channel: number }, { rejectWithValue }) => {
      try {
        const groupTree = await GetProjectGroupTree(channel);
        return groupTree;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      pending: (state) => {
        state.gettingGroupTree = true;
      },
      fulfilled: (state, action) => {
        state.projectGroupTree = action.payload.tree;
        state.projectGroupTreeNameMap = action.payload.nameMap;
      },
      rejected: (state) => {
        state.projectGroupTree = { ungrouped: { events: {} } } as EventGroupTreeEntry;
        state.projectGroupTreeNameMap = {};
      },
      settled: (state) => {
        state.gettingGroupTree = false;
      },
    }
  ),
  modifyProjectGroupsForAdmin: create.asyncThunk(
    async (params: ModifyProjectGroupsForAdminProps, { rejectWithValue }) => {
      try {
        const res = await ModifyProjectGroupsForAdmin(params);
        return res;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.projectGroups) {
          const { added, deleted, adminId } = action.payload;
          state.projectGroups = state.projectGroups.map((pg) => {
            if (added.includes(pg.uuid)) {
              return {
                ...pg,
                adminIds: [...pg.adminIds, adminId],
              };
            }
            if (deleted.includes(pg.uuid)) {
              return {
                ...pg,
                adminIds: pg.adminIds.filter((admin) => admin !== adminId),
              };
            }
            return pg;
          });
        }
      },
      rejected: (state) => {
        state.groupError = 'Error modifying event folders for admin';
      },
    }
  ),
});
